type StyleSnapshot = {
  overflow: string;
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
  paddingRight: string;
  overscrollBehavior: string;
};

export type OverlayScrollLockOptions = {
  // Pin <body> with position:fixed and restore the page offset on release.
  useFixedPosition?: boolean;
  // Also hide <html> overflow (the compact header drawer relies on this).
  lockRootOverflow?: boolean;
  // Counted by getOverlayScrollLockCount() and OVERLAY_LOCK_CHANGE_EVENT. The
  // header's own drawer opts out: that signal means "an overlay outside the
  // header opened" and makes the header close its menus.
  notifyOverlayChange?: boolean;
};

type OverlayScrollLockHold = Required<OverlayScrollLockOptions> & {
  id: number;
};

type OverlayScrollLockState = {
  nextHoldId: number;
  holds: OverlayScrollLockHold[];
  anonymousReleases: Array<() => void>;
  lockedScrollY: number;
  appliedFixedPosition: boolean;
  appliedRootOverflow: boolean;
  bodyStyles: StyleSnapshot;
  rootOverflow: string;
  rootScrollbarCompensation: string;
  rootLockedScrollY: string;
};

// Every overlay (cart/filter drawers, bottom sheets, the compact header menu)
// shares one owner-aware lock: page styles are captured when the first hold is
// taken, adjusted as holds with different needs come and go, and restored only
// when the last hold is released. Separate owners must never snapshot or
// restore each other's inline styles.
const OVERLAY_SCROLL_LOCK_KEY = "__vfOverlayScrollLockStateV2__";
export const OVERLAY_LOCK_CHANGE_EVENT = "vf:overlay-lock-change";
export const OVERLAY_SCROLLBAR_COMPENSATION_VAR =
  "--vf-overlay-scrollbar-compensation";
export const OVERLAY_LOCKED_SCROLL_Y_VAR = "--vf-overlay-locked-scroll-y";

const createInitialState = (): OverlayScrollLockState => ({
  nextHoldId: 1,
  holds: [],
  anonymousReleases: [],
  lockedScrollY: 0,
  appliedFixedPosition: false,
  appliedRootOverflow: false,
  rootOverflow: "",
  rootScrollbarCompensation: "",
  rootLockedScrollY: "",
  bodyStyles: {
    overflow: "",
    position: "",
    top: "",
    left: "",
    right: "",
    width: "",
    paddingRight: "",
    overscrollBehavior: "",
  },
});

const getOverlayScrollLockState = (): OverlayScrollLockState | null => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }

  const globalWindow = window as Window & {
    [OVERLAY_SCROLL_LOCK_KEY]?: OverlayScrollLockState;
  };

  if (!globalWindow[OVERLAY_SCROLL_LOCK_KEY]) {
    globalWindow[OVERLAY_SCROLL_LOCK_KEY] = createInitialState();
  }

  return globalWindow[OVERLAY_SCROLL_LOCK_KEY] || null;
};

const countNotifyingHolds = (state: OverlayScrollLockState) =>
  state.holds.filter((hold) => hold.notifyOverlayChange).length;

const emitOverlayLockChange = (count: number) => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent(OVERLAY_LOCK_CHANGE_EVENT, {
      detail: { count },
    })
  );
};

export const getOverlayScrollLockCount = () => {
  const state = getOverlayScrollLockState();
  return state ? countNotifyingHolds(state) : 0;
};

const parsePxValue = (value: string) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const readWindowScrollY = () => window.scrollY || window.pageYOffset || 0;

const restoreRootProperty = (name: string, value: string) => {
  if (value) {
    document.documentElement.style.setProperty(name, value);
  } else {
    document.documentElement.style.removeProperty(name);
  }
};

const engageLock = (state: OverlayScrollLockState) => {
  const bodyStyle = document.body.style;
  const rootStyle = document.documentElement.style;

  state.lockedScrollY = readWindowScrollY();
  state.bodyStyles = {
    overflow: bodyStyle.overflow,
    position: bodyStyle.position,
    top: bodyStyle.top,
    left: bodyStyle.left,
    right: bodyStyle.right,
    width: bodyStyle.width,
    paddingRight: bodyStyle.paddingRight,
    overscrollBehavior: bodyStyle.overscrollBehavior,
  };
  state.rootOverflow = rootStyle.overflow;
  state.rootScrollbarCompensation = rootStyle.getPropertyValue(
    OVERLAY_SCROLLBAR_COMPENSATION_VAR
  );
  state.rootLockedScrollY = rootStyle.getPropertyValue(
    OVERLAY_LOCKED_SCROLL_Y_VAR
  );

  const scrollbarWidth = Math.max(
    window.innerWidth - document.documentElement.clientWidth,
    0
  );
  // Keep layout width stable when body scrollbar disappears under overlay lock
  // (notably on Windows where scrollbars take visual space).
  if (scrollbarWidth > 0) {
    const computedPaddingRight = window.getComputedStyle(
      document.body
    ).paddingRight;
    const nextPaddingRight =
      parsePxValue(computedPaddingRight) + scrollbarWidth;
    bodyStyle.paddingRight = `${nextPaddingRight}px`;
  }
  rootStyle.setProperty(
    OVERLAY_SCROLLBAR_COMPENSATION_VAR,
    `${scrollbarWidth}px`
  );
  rootStyle.setProperty(
    OVERLAY_LOCKED_SCROLL_Y_VAR,
    `${state.lockedScrollY}px`
  );

  bodyStyle.overflow = "hidden";
  bodyStyle.overscrollBehavior = "none";
};

const applyFixedPosition = (state: OverlayScrollLockState) => {
  const bodyStyle = document.body.style;

  // An overflow-only hold keeps the document offset, so the current offset is
  // still the position the visitor sees.
  state.lockedScrollY = readWindowScrollY();
  document.documentElement.style.setProperty(
    OVERLAY_LOCKED_SCROLL_Y_VAR,
    `${state.lockedScrollY}px`
  );
  bodyStyle.position = "fixed";
  bodyStyle.top = `-${state.lockedScrollY}px`;
  bodyStyle.left = "0";
  bodyStyle.right = "0";
  bodyStyle.width = "100%";
  state.appliedFixedPosition = true;
};

const releaseFixedPosition = (state: OverlayScrollLockState) => {
  const bodyStyle = document.body.style;

  bodyStyle.position = state.bodyStyles.position;
  bodyStyle.top = state.bodyStyles.top;
  bodyStyle.left = state.bodyStyles.left;
  bodyStyle.right = state.bodyStyles.right;
  bodyStyle.width = state.bodyStyles.width;
  state.appliedFixedPosition = false;
  window.scrollTo(0, state.lockedScrollY);
};

const restoreLock = (state: OverlayScrollLockState) => {
  const bodyStyle = document.body.style;
  const restoreScrollPosition = state.appliedFixedPosition;

  bodyStyle.overflow = state.bodyStyles.overflow;
  bodyStyle.position = state.bodyStyles.position;
  bodyStyle.top = state.bodyStyles.top;
  bodyStyle.left = state.bodyStyles.left;
  bodyStyle.right = state.bodyStyles.right;
  bodyStyle.width = state.bodyStyles.width;
  bodyStyle.paddingRight = state.bodyStyles.paddingRight;
  bodyStyle.overscrollBehavior = state.bodyStyles.overscrollBehavior;
  document.documentElement.style.overflow = state.rootOverflow;
  restoreRootProperty(
    OVERLAY_SCROLLBAR_COMPENSATION_VAR,
    state.rootScrollbarCompensation
  );
  restoreRootProperty(OVERLAY_LOCKED_SCROLL_Y_VAR, state.rootLockedScrollY);
  state.appliedFixedPosition = false;
  state.appliedRootOverflow = false;
  if (restoreScrollPosition) {
    window.scrollTo(0, state.lockedScrollY);
  }
};

const syncLockStyles = (state: OverlayScrollLockState, wasEngaged: boolean) => {
  if (state.holds.length === 0) {
    if (wasEngaged) restoreLock(state);
    return;
  }

  if (!wasEngaged) engageLock(state);

  const needsFixedPosition = state.holds.some((hold) => hold.useFixedPosition);
  if (needsFixedPosition && !state.appliedFixedPosition) {
    applyFixedPosition(state);
  } else if (!needsFixedPosition && state.appliedFixedPosition) {
    releaseFixedPosition(state);
  }

  const needsRootOverflow = state.holds.some((hold) => hold.lockRootOverflow);
  if (needsRootOverflow !== state.appliedRootOverflow) {
    document.documentElement.style.overflow = needsRootOverflow
      ? "hidden"
      : state.rootOverflow;
    state.appliedRootOverflow = needsRootOverflow;
  }
};

const noopRelease = () => undefined;

// Takes one hold and returns its release. The release is idempotent, so an
// owner can call it from close, timers and unmount without double-unlocking.
export const acquireOverlayScrollLock = ({
  useFixedPosition = true,
  lockRootOverflow = false,
  notifyOverlayChange = true,
}: OverlayScrollLockOptions = {}): (() => void) => {
  const state = getOverlayScrollLockState();
  if (!state) return noopRelease;

  const hold: OverlayScrollLockHold = {
    id: state.nextHoldId,
    useFixedPosition,
    lockRootOverflow,
    notifyOverlayChange,
  };
  state.nextHoldId += 1;

  const wasEngaged = state.holds.length > 0;
  state.holds.push(hold);
  syncLockStyles(state, wasEngaged);
  if (notifyOverlayChange) emitOverlayLockChange(countNotifyingHolds(state));

  let released = false;
  return () => {
    if (released) return;
    released = true;

    const index = state.holds.findIndex(({ id }) => id === hold.id);
    if (index === -1) return;

    state.holds.splice(index, 1);
    syncLockStyles(state, true);
    if (notifyOverlayChange) emitOverlayLockChange(countNotifyingHolds(state));
  };
};

// Compatibility API for owners that track a single hold themselves
// (bottom-sheet). Each unlock releases the most recent anonymous hold.
export const lockOverlayScroll = ({
  useFixedPosition = true,
}: {
  useFixedPosition?: boolean;
} = {}) => {
  const state = getOverlayScrollLockState();
  if (!state) return;

  state.anonymousReleases.push(acquireOverlayScrollLock({ useFixedPosition }));
};

export const unlockOverlayScroll = () => {
  const state = getOverlayScrollLockState();
  const release = state?.anonymousReleases.pop();
  release?.();
};
